/**
 * SponsorChildDetailPage — /sponsor/child/:userId
 * Shows grouped subject progress for a sponsored child via the Privacy Engine.
 * Sponsors view sanitized progress snapshots without direct access to raw student records.
 */
import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft, ChevronDown, ChevronRight, BookOpen,
  Trophy, Loader2, AlertTriangle, Star,
} from 'lucide-react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

interface ModuleLesson {
  lesson_number: number
  title: string
}

interface ModuleData {
  unit: number
  title: string
  lessons: ModuleLesson[]
}

interface SubjectProgress {
  id: string
  category_id: string
  category_name: string
  category_icon: string
  subject_group: string | null
  current_unit: number
  current_lesson: number
  mastery_score: number
  class_id: string | null
  modules?: ModuleData[]
}

interface QuizResult {
  id: string
  category_id: string | null
  class_id: string | null
  lesson_unit: number
  lesson_number: number
  score_percentage: number
  attempted_at?: string | null
}

export default function SponsorChildDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { t } = useTranslation()
  const supabase = createClientComponentClient()

  const [childName, setChildName] = useState<string>('Sponsored Child')
  const [subjects, setSubjects] = useState<SubjectProgress[]>([])
  const [quizResults, setQuizResults] = useState<QuizResult[]>([])
  const [totalLessons, setTotalLessons] = useState<number>(0)
  const [overallMastery, setOverallMastery] = useState<number>(0)
  const [quizAvg, setQuizAvg] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null)
  const [expandedModule, setExpandedModule] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!userId || !user) return
    setLoading(true)
    setError(null)

    try {
      // 1. Verify this sponsor owns an active seat assignment for this user
      const { data: assignments, error: aError } = await supabase
        .from('sponsor_seat_assignments')
        .select(`
          id, sponsorship_id, assigned_name, assigned_email,
          facility_seat_sponsorships:sponsorship_id (sponsor_id, update_preference)
        `)
        .eq('assigned_user_id', userId)
        .eq('status', 'active')

      if (aError || !assignments?.length) {
        setError(t('sponsor.childDetail.noSponsorshipFound', 'No active sponsorship found for this learner.'))
        return
      }

      // Confirm ownership
      const assignment = assignments.find((a: any) => a.facility_seat_sponsorships?.sponsor_id === user.id)
      if (!assignment) {
        setError(t('sponsor.childDetail.notAuthorized', 'You are not authorized to view this learner.'))
        return
      }

      // Privacy Engine check: only reveal student name if sponsor chose full update preference
      const updatePref = (assignment as any).facility_seat_sponsorships?.update_preference ?? 'none'
      if (updatePref === 'full' && assignment.assigned_name) {
        setChildName(assignment.assigned_name)
      } else {
        setChildName(t('sponsor.dashboard.sponsoredChild', 'Sponsored Child'))
      }

      // 2. Fetch latest sanitized snapshot from sponsor_progress_snapshots
      let { data: snap } = await supabase
        .from('sponsor_progress_snapshots')
        .select('*')
        .eq('sponsorship_id', assignment.sponsorship_id)
        .eq('assigned_user_id', userId)
        .order('snapshot_date', { ascending: false })
        .limit(1)
        .maybeSingle()

      // If snapshot doesn't exist yet, trigger on-demand sync via edge function
      if (!snap) {
        try {
          await supabase.functions.invoke('sync-sponsor-snapshots', {
            body: { assigned_user_id: userId }
          })
          const { data: freshSnap } = await supabase
            .from('sponsor_progress_snapshots')
            .select('*')
            .eq('sponsorship_id', assignment.sponsorship_id)
            .eq('assigned_user_id', userId)
            .order('snapshot_date', { ascending: false })
            .limit(1)
            .maybeSingle()
          snap = freshSnap
        } catch (syncErr) {
          console.warn('Sync snapshot on-demand failed:', syncErr)
        }
      }

      if (snap) {
        setTotalLessons(snap.lessons_completed ?? 0)
        setOverallMastery(Math.round(snap.mastery_score ?? 0))
        setQuizAvg(Math.round(snap.quiz_avg_score ?? 0))

        const snapData: any = snap.snapshot_data || {}
        const snapSubjects: SubjectProgress[] = snapData.subjects || []
        const snapQuizzes: QuizResult[] = snapData.quizzes || []

        setSubjects(snapSubjects)
        setQuizResults(snapQuizzes)
      } else {
        setTotalLessons(0)
        setOverallMastery(0)
        setQuizAvg(0)
        setSubjects([])
        setQuizResults([])
      }
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [userId, user, supabase, t])

  useEffect(() => { load() }, [load])

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <Loader2 className="animate-spin text-primary" size={36} />
    </div>
  )

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-6">
      <div className="text-center space-y-4">
        <AlertTriangle size={40} className="text-destructive mx-auto" />
        <p className="font-bold text-foreground">{error}</p>
        <button onClick={() => navigate('/sponsor/dashboard')}
          className="px-6 py-3 bg-primary text-primary-foreground font-bold rounded-xl">
          {t('sponsor.childDetail.backToDashboard', 'Back to Dashboard')}
        </button>
      </div>
    </div>
  )

  // Group subjects by department
  const grouped: Record<string, SubjectProgress[]> = {}
  for (const s of subjects) {
    const group = s.subject_group || 'Core Subjects'
    if (!grouped[group]) grouped[group] = []
    grouped[group].push(s)
  }
  const sortedGroups = Object.keys(grouped).sort()

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-emerald-700 text-white py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/sponsor/dashboard')}
            className="flex items-center gap-2 text-emerald-200 hover:text-white text-sm font-bold mb-4 transition-colors">
            <ChevronLeft size={16} /> {t('sponsor.childDetail.backToDashboard', 'Back to Dashboard')}
          </button>
          <h1 className="text-2xl font-black">{childName} — {t('sponsor.childDetail.progress', 'Progress Overview')}</h1>
          <p className="text-emerald-200 text-sm mt-1">{subjects.length} {t('sponsor.childDetail.subjectsEnrolled', 'Subjects Enrolled')}</p>

          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
              <p className="text-2xl font-black">{totalLessons}</p>
              <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-wider">{t('sponsor.childDetail.lessons', 'Lessons Completed')}</p>
            </div>
            <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
              <p className="text-2xl font-black">{overallMastery}%</p>
              <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-wider">{t('sponsor.childDetail.mastery', 'Curriculum Mastery')}</p>
            </div>
            <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
              <p className="text-2xl font-black">{quizAvg}%</p>
              <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-wider">{t('sponsor.childDetail.quizAvg', 'Quiz Average')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {sortedGroups.map(group => (
          <div key={group}>
            <h2 className="text-sm font-black text-muted-foreground uppercase tracking-widest mb-3">{group}</h2>
            <div className="space-y-3">
              {grouped[group].map(s => {
                const isExpanded = expandedSubject === s.id
                const subjectQuizzes = quizResults.filter(q => q.category_id === s.category_id)
                const modules = s.modules || []

                return (
                  <div key={s.id} className="bg-card rounded-2xl border border-border overflow-hidden">
                    <button onClick={() => setExpandedSubject(isExpanded ? null : s.id)}
                      className="w-full p-4 flex items-center gap-3 text-left hover:bg-accent/50 transition-colors">
                      <span className="text-xl">{s.category_icon}</span>
                      <div className="flex-1">
                        <p className="font-bold text-card-foreground text-sm">{s.category_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('sponsor.childDetail.unit', 'Unit')} {s.current_unit} · {t('sponsor.childDetail.lesson', 'Lesson')} {s.current_lesson} · {Math.round(s.mastery_score)}% {t('sponsor.childDetail.masteryPercent', 'Mastery')}
                        </p>
                      </div>
                      <div className="w-16">
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, s.mastery_score)}%` }} />
                        </div>
                      </div>
                      <ChevronDown size={16} className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                          className="overflow-hidden border-t border-border">
                          <div className="p-4 space-y-3">
                            {modules.length > 0 ? modules.map(mod => {
                              const modKey = `${s.id}-${mod.unit}`
                              const isModExpanded = expandedModule === modKey
                              const modQuizzes = subjectQuizzes.filter(q => q.lesson_unit === mod.unit)
                              const completed = mod.unit < s.current_unit || (mod.unit === s.current_unit && s.current_lesson > (mod.lessons?.length || 1))
                              const current = mod.unit === s.current_unit

                              return (
                                <div key={modKey} className="bg-muted/50 rounded-xl overflow-hidden">
                                  <button onClick={() => setExpandedModule(isModExpanded ? null : modKey)}
                                    className="w-full p-3 flex items-center gap-2 text-left">
                                    {completed ? <Trophy size={14} className="text-amber-500" /> :
                                      current ? <BookOpen size={14} className="text-primary" /> :
                                      <Star size={14} className="text-muted-foreground" />}
                                    <span className="flex-1 text-sm font-bold text-foreground">{mod.title}</span>
                                    {modQuizzes.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        {modQuizzes.length} {modQuizzes.length > 1 ? t('sponsor.childDetail.quizzes', 'Quizzes') : t('sponsor.childDetail.quiz', 'Quiz')}
                                      </span>
                                    )}
                                    <ChevronRight size={14} className={`text-muted-foreground transition-transform ${isModExpanded ? 'rotate-90' : ''}`} />
                                  </button>

                                  <AnimatePresence>
                                    {isModExpanded && (
                                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                                        className="overflow-hidden">
                                        <div className="px-3 pb-3 space-y-2">
                                          {mod.lessons?.map((lesson: ModuleLesson, li: number) => {
                                            const lessonQuiz = modQuizzes.find(q => q.lesson_number === (lesson.lesson_number || li + 1))
                                            return (
                                              <div key={li} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                                                <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                                                  {lesson.lesson_number || li + 1}
                                                </span>
                                                <span className="flex-1 text-xs text-foreground">
                                                  {lesson.title || `${t('sponsor.childDetail.lesson', 'Lesson')} ${li + 1}`}
                                                </span>
                                                {lessonQuiz && (
                                                  <span className={`text-xs font-bold ${lessonQuiz.score_percentage >= 80 ? 'text-emerald-600' : lessonQuiz.score_percentage >= 60 ? 'text-amber-600' : 'text-red-500'}`}>
                                                    {lessonQuiz.score_percentage}%
                                                  </span>
                                                )}
                                              </div>
                                            )
                                          })}
                                          {modQuizzes.length === 0 && (
                                            <p className="text-xs text-muted-foreground italic py-1">{t('sponsor.childDetail.noQuizzes', 'No quizzes completed yet.')}</p>
                                          )}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              )
                            }) : (
                              <p className="text-xs text-muted-foreground italic">{t('sponsor.childDetail.noModuleData', 'Curriculum syllabus currently synchronizing.')}</p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {subjects.length === 0 && (
          <div className="text-center py-12">
            <BookOpen size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="font-bold text-foreground">{t('sponsor.childDetail.noSubjects', 'No subjects enrolled yet')}</p>
            <p className="text-sm text-muted-foreground">{t('sponsor.childDetail.noSubjectsDesc', 'When subjects are assigned to this student, progress will show here.')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
